import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, ArrowRight, Shield, Target, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import api, { API } from "../lib/api";

const fmtDate = (s) => {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
};

const Ledger = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [selected, setSelected] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/ledger/public/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = selected ? { campaign_id: selected, limit: 200 } : { limit: 200 };
        api.get("/ledger/public", { params })
            .then((r) => setLedger(r.data))
            .finally(() => setLoading(false));
    }, [selected]);

    const totals = ledger?.totals || { in: 0, out: 0, available: 0 };

    return (
        <div data-testid="ledger-page" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-20">
            <SEO
                title="Public ledger · Every dollar in, every dollar out"
                description="Live, public, line-item-level accounting of every donation received and every dollar spent by Reddog Community Foundation. Transparency is our default."
                path="/ledger"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Dataset",
                    name: "Reddog Community Foundation Public Ledger",
                    description:
                        "Live, line-item-level public dataset of all donations received and all payouts made by Reddog Community Foundation Inc. Updated in real time.",
                    license: "https://creativecommons.org/licenses/by/4.0/",
                    keywords: ["nonprofit transparency", "public ledger", "donations", "501(c)(3) pending"],
                    creator: { "@type": "NGO", name: "Reddog Community Foundation Inc." },
                }}
            />
            <div className="mb-12">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                    Public ledger · Fund transparency
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-6">
                    Every dollar in.
                    <br />
                    <span className="text-[#E53935]">Every dollar out.</span>
                </h1>
                <p className="text-lg text-[#4A5568] leading-relaxed max-w-3xl">
                    The Reddog Community Foundation publishes its full donation ledger and approved
                    payouts in real time. Filter by fundraiser to see exactly how donations were raised
                    and where they were spent — vendor names, dates, dual sign-off counts, the works.
                </p>
                <p className="mt-3 text-xs text-[#4A5568] max-w-3xl">
                    <strong>Scope:</strong> this ledger tracks <em>donations received</em> and <em>approved spend-request payouts</em> only.
                    Merchandise sales (net proceeds to the General Support Fund) are summarized on the{" "}
                    <Link to="/" className="text-[#E53935] underline-offset-2 hover:underline font-medium">homepage tracker</Link>.
                </p>
            </div>

            {/* Headline totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="ledger-totals">
                <div className="bg-white border border-black/5 rounded-2xl p-6">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">Donations received (ledger)</p>
                    <p className="font-heading text-3xl text-[#1C252E] font-medium">${totals.in.toLocaleString()}</p>
                    <p className="text-[11px] text-[#4A5568] mt-1">Excludes merchandise revenue</p>
                </div>
                <div className="bg-white border border-black/5 rounded-2xl p-6">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">Approved payouts disbursed</p>
                    <p className="font-heading text-3xl text-[#1C252E] font-medium">${totals.out.toLocaleString()}</p>
                    <p className="text-[11px] text-[#4A5568] mt-1">Dual sign-off, paid invoices</p>
                </div>
                {(() => {
                    const isGap = totals.available < 0;
                    const gapAmt = Math.abs(totals.available);
                    return (
                        <div
                            data-testid="ledger-available-card"
                            className={`rounded-2xl p-6 border ${
                                isGap
                                    ? "bg-amber-50 border-amber-300"
                                    : "bg-[#F2D8D5] border-[#E53935]/20"
                            }`}
                        >
                            <p className={`text-xs uppercase tracking-wider font-bold mb-2 ${isGap ? "text-amber-800" : "text-[#E53935]"}`}>
                                {isGap ? "Pending funding gap" : "Available for approved purposes"}
                            </p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium" data-testid="ledger-available-amount">
                                ${(isGap ? gapAmt : totals.available).toLocaleString()}
                            </p>
                            <p className="text-[11px] text-[#4A5568] mt-1">
                                {isGap
                                    ? "Payouts approved exceed donations on hand — covered by reserves, pledges, or pending reimbursement."
                                    : "Ledger balance, ready to deploy"}
                            </p>
                        </div>
                    );
                })()}
            </div>

            {/* Privacy / scope disclosure */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-10 text-sm text-[#4A5568] leading-relaxed" data-testid="ledger-privacy-note">
                <p>
                    <strong className="text-amber-900">Public-facing summary:</strong> This ledger is a public summary of donations received and payouts disbursed. It does not expose private contractor registry data, volunteer credentials, beneficiary personal information, internal notes, or protected documents.
                </p>
            </div>

            {/* Campaign filter */}
            {campaigns.length > 0 && (
                <div className="mb-8" data-testid="ledger-campaign-filter">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-3">Filter by fundraiser</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => setSelected(null)}
                            data-testid="ledger-filter-all"
                            className={`text-xs px-4 py-2 rounded-full border transition-colors ${
                                !selected
                                    ? "bg-[#E53935] border-[#E53935] text-white"
                                    : "border-black/10 hover:border-[#E53935]"
                            }`}
                        >
                            All fundraisers
                        </button>
                        {campaigns.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setSelected(c.id)}
                                data-testid={`ledger-filter-${c.slug}`}
                                className={`text-xs px-4 py-2 rounded-full border transition-colors flex items-center gap-2 ${
                                    selected === c.id
                                        ? "bg-[#E53935] border-[#E53935] text-white"
                                        : "border-black/10 hover:border-[#E53935]"
                                }`}
                                title={`${c.category} · raised $${c.raised.toLocaleString()} · available $${c.available.toLocaleString()}`}
                            >
                                <Target className="w-3 h-3" strokeWidth={2} />
                                {c.name}
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${selected === c.id ? "bg-white/20" : "bg-[#F4F1EA]"}`}>
                                    ${c.available.toLocaleString()}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Ledger entries */}
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="ledger-entries-card">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-6 py-3 font-medium">Date</th>
                                <th className="text-left px-4 py-3 font-medium">Type</th>
                                <th className="text-left px-4 py-3 font-medium">Detail</th>
                                <th className="text-right px-4 py-3 font-medium">Amount</th>
                                <th className="text-right px-6 py-3 font-medium">Running balance</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {loading && (
                                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#4A5568]">Loading…</td></tr>
                            )}
                            {!loading && (!ledger || ledger.entries.length === 0) && (
                                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#4A5568]">No entries yet for this filter.</td></tr>
                            )}
                            {!loading && ledger?.entries.map((e, i) => {
                                const clickable = e.kind === "out" && e.id;
                                return (
                                <tr
                                    key={i}
                                    data-testid={`ledger-row-${i}`}
                                    onClick={clickable ? () => navigate(`/ledger/spend/${e.id}`) : undefined}
                                    className={`${clickable ? "hover:bg-[#F2D8D5]/40 cursor-pointer" : "hover:bg-[#F4F1EA]/40"}`}
                                >
                                    <td className="px-6 py-3 text-[#4A5568] whitespace-nowrap">{fmtDate(e.at)}</td>
                                    <td className="px-4 py-3">
                                        {e.kind === "in" ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                <TrendingUp className="w-3 h-3" strokeWidth={2} />
                                                In
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                <TrendingDown className="w-3 h-3" strokeWidth={2} />
                                                Out
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {e.kind === "in" ? (
                                            <span className="text-[#1C252E]">{e.name || "Anonymous"}</span>
                                        ) : (
                                            <div>
                                                <p className="text-[#1C252E] font-medium inline-flex items-center gap-2 flex-wrap">
                                                    {e.purpose_public || e.title}
                                                    {(e.before_photos_count > 0 || e.after_photos_count > 0) && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                                            <ImageIcon className="w-2.5 h-2.5" /> {e.before_photos_count + e.after_photos_count}
                                                        </span>
                                                    )}
                                                    {e.has_completion_proof && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                                            <CheckCircle2 className="w-2.5 h-2.5" /> Proof
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-[#4A5568]">
                                                    Paid to {e.vendor}
                                                    {e.requested_by && ` · req. by ${e.requested_by}`}
                                                    {typeof e.approvals === "number" && ` · ${e.approvals} sign-off${e.approvals === 1 ? "" : "s"}`}
                                                    {clickable && <span className="ml-1 text-[#E53935] font-medium">· View details →</span>}
                                                </p>
                                            </div>
                                        )}
                                    </td>
                                    <td className={`px-4 py-3 text-right font-mono font-medium ${e.kind === "in" ? "text-green-700" : "text-[#E53935]"}`}>
                                        {e.kind === "in" ? "+" : "−"}${e.amount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-3 text-right font-mono text-[#4A5568]">
                                        ${(e.balance_after ?? 0).toLocaleString()}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-12 bg-[#F4F1EA] rounded-3xl p-10 text-center">
                <h2 className="font-heading text-2xl text-[#1C252E] font-medium mb-3">Our promise</h2>
                <p className="text-[#4A5568] max-w-2xl mx-auto leading-relaxed mb-6">
                    Every payout above goes through a two-person sign-off — typically a foundation admin and a board
                    member — before a dollar leaves the account. Vendors are pre-vetted, invoices are kept on file,
                    and the ledger you see here supports the foundation’s internal review, board oversight, and
                    year-end reporting.
                </p>
                <Link to="/about" className="inline-flex items-center gap-2 text-sm font-medium text-[#E53935] hover:underline">
                    Learn more about our governance
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
            </div>

            {/* Open data — let journalists, auditors, and researchers download the raw ledger */}
            <div className="mt-8 bg-white border border-black/10 rounded-3xl p-8" data-testid="ledger-open-data">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#1C252E] mb-2">Public ledger export</p>
                        <p className="text-sm text-[#4A5568] max-w-2xl leading-relaxed">
                            Download the public ledger as a CSV or JSON file. Donor names appear only as shown on the public ledger above — private contractor and beneficiary records are excluded. Updated live as donations come in and payouts clear.
                        </p>
                    </div>
                    <div className="flex gap-3 flex-shrink-0">
                        <a
                            href={`${API}/public/data.csv`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="ledger-open-data-csv"
                            className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                        >
                            Download CSV
                        </a>
                        <a
                            href={`${API}/public/data.json`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="ledger-open-data-json"
                            className="inline-flex items-center gap-2 bg-transparent border border-[#1C252E] text-[#1C252E] hover:bg-[#1C252E] hover:text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                        >
                            Download JSON
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Ledger;
